import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import 'firebase/auth';
import {
  confirmPasswordReset,
  getAuth,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  User,
  verifyPasswordResetCode
} from 'firebase/auth';

import { collection, doc, getDoc, getFirestore } from 'firebase/firestore';
import { BehaviorSubject, Subject } from 'rxjs';
import { Processor } from '../../models';
import { SnackbarService } from './snackbar.service';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  public showLoginLoader = new Subject<boolean>();
  private sessionTokenSource: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _processorUid: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _loggedIn: BehaviorSubject<boolean>;
  private _processorData: BehaviorSubject<Processor>;
  private _activatedRoute: string;
  private _resetPasswordCode: string;
  private _isLoggingOut: boolean = false;

  constructor(private router: Router, private snackbarService: SnackbarService
  ) {
    this._loggedIn = new BehaviorSubject<boolean>(null);
    this.showLoginLoader = new Subject<boolean>();
    this.sessionTokenSource = new BehaviorSubject<string>(null);
    this._activatedRoute = '';
    this._processorData = new BehaviorSubject<any>(null);
    this.initAuthListener();
  }

  get sessionTokenRaw() {
    return this.sessionTokenSource.value;
  }

  get sessionToken$() {
    return this.sessionTokenSource
      .asObservable();
  }

  get currentProcessorUid() {
    return this._processorUid.value;
  }

  login(email: string, password: string): Promise<boolean | any> {
    const auth = getAuth();
    return new Promise((resolve, reject) => {
      signInWithEmailAndPassword(auth, email, password)
        .then(user => {
          resolve(user);
        })
        .catch(e => {
          reject(e);
        });
    });
  }

  initAuthListener() {
    try {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user: User) => {
        this._activatedRoute = window.location.pathname;

        if (!!user && !user.isAnonymous) {
          const processorAccess = await this.initProcessorAuth(user);
          this.setLoggedInState(false);
          user = processorAccess ? user : null;
        }

        console.log('Auth state changed. User:', user);

        if (!!user && !!user.email) {
          console.log('User is logged in with email:', user.email);
          this.setLoggedInState(true);
          if (this._activatedRoute === '/login' || this._activatedRoute === '/') {
            this.router.navigate(['/home/jobs']);
          } else {
            this.router.navigate([this._activatedRoute]);
          }
        } else {
          this.setLoggedInState(false);
          this.router.navigate(['/login']);
        }
      });
    } catch (e) {
      console.error(e);
    }
  }

  get hasInvoicingPermissions() {
    return this._processorData.getValue().invoicingPermissions;
  }

  async initProcessorAuth(user: User) {
    if (!!user && !user.isAnonymous) {
      if (user?.uid) {

        // Check if user has access to member portal (redundant check for safety)
        const idTokenResult = await user.getIdTokenResult();
        const portals = idTokenResult.claims['portals'] as string[];

        if (!portals || !portals.includes('processor')) {
          console.error('User does not have access to processor portal. Portals:', portals);
          this.showLoginLoader.next(false);
          this.snackbarService.showSnackbar('Access denied. You do not have permission to access the processor portal.');
          // Sign out and redirect to login
          await getAuth().signOut();
          return false;
        }

        const sessionToken = await user.getIdToken(true); // Force refresh to get fresh token
        this.sessionTokenSource.next(sessionToken);
        this._processorUid.next(user.uid);

        // Set up token refresh every 50 minutes (tokens expire after 60 minutes)
        this.setupTokenRefresh(user);

        const firestoreRef = getFirestore();
        const processorProfilesCollection = collection(firestoreRef, 'processorProfiles');
        const processorProfileDocRef = doc(processorProfilesCollection, user.uid);
        const processorProfileSnapshot = await getDoc(processorProfileDocRef);
        this._processorData.next(processorProfileSnapshot.data() as Processor);

        
      }

      return true;
    }
  }

  private tokenRefreshInterval: any;

  private setupTokenRefresh(user: User) {
    // Clear any existing interval
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
    }

    // Refresh token every 50 minutes (3000000 ms)
    this.tokenRefreshInterval = setInterval(async () => {
      try {
        const newToken = await user.getIdToken(true);
        this.sessionTokenSource.next(newToken);
        console.log('Firebase token refreshed successfully');
      } catch (error) {
        console.error('Error refreshing Firebase token:', error);
        // If refresh fails, user might need to re-authenticate
        this.logout();
      }
    }, 3000000); // 50 minutes
  }

  resetPassword(email: string): Promise<void> {
    const auth = getAuth();
    return new Promise((resolve, reject) => {
      sendPasswordResetEmail(auth, email)
        .then(() => resolve())
        .catch(e => reject(e));
    });
  }

  async updatePassword(newPassword: string) {
    let userEmail = '';
    let auth = getAuth();
    this.snackbarService.showSnackbar('Updating password...');
    const code = this._resetPasswordCode;
    try {
      userEmail = await verifyPasswordResetCode(auth, code);
      await confirmPasswordReset(auth, code, newPassword);
      this.snackbarService.showSnackbar('Your password has been reset. Logging you back in...');
      await this.login(userEmail, newPassword);
      return this.goHome();
    } catch (e) {
      console.error('Could not reset password:', e);
      this.snackbarService.handleError('An error occurred while trying to update your password');
      return false;
    }
  }

  goHome() {
    const member = this._processorData?.value;
    if (!!member) {
      this.setLoggedInState(true);
      this.router.navigate(['/home/jobs']);
    } else {
      this.logout();
    }
  }

  logout() {
    // Clear token refresh interval on logout
    if (this.tokenRefreshInterval) {
      clearInterval(this.tokenRefreshInterval);
      this.tokenRefreshInterval = null;
    }
    this._loggedIn.next(false);
    this.sessionTokenSource.next(null);
    this._processorData.next(null);
    this._processorUid.next(null);
    try {
      sessionStorage.removeItem('prism-jobs-filter');
    } catch {
    }
    return getAuth().signOut();
  }

  setLoggedInState(value: boolean) {
    this._loggedIn.next(value);
  }

  isLoggedIn$() {
    return this._loggedIn.asObservable();
  }

  isLoggedIn() {
    return this._loggedIn.getValue();
  }

  processorProfileData$() {
    return this._processorData.asObservable();
  }
}
